import React, { useEffect, useMemo, useState } from "react";
import { useAlert } from "react-alert";
import {
  CloudDownloadOutlined,
  DescriptionOutlined,
  ErrorOutline,
  FolderOpenOutlined,
  InsertDriveFileOutlined,
  PictureAsPdfOutlined,
  Refresh,
  SchoolOutlined,
  Search,
} from "@material-ui/icons";
import { useParent } from "../../../../context/ParentContext";
import { baseUrl } from "../../../../services/config";
import "../MonthlyStudentAttendance/parent-progress.css";
import "./school-resources.css";

const getClassId = (child) =>
  child?.student_class_id ||
  child?.student?.student_class_id ||
  child?.student?.student_class?.id ||
  null;

function formatDate(value) {
  if (!value) return "Date unavailable";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Date unavailable";
  return new Intl.DateTimeFormat("en-PK", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

function fileInfo(fileName = "") {
  const extension = fileName.includes(".")
    ? fileName
        .split(".")
        .pop()
        .toUpperCase()
    : "FILE";
  if (extension === "PDF")
    return { extension, icon: PictureAsPdfOutlined, tone: "pdf" };
  if (["DOC", "DOCX"].includes(extension))
    return { extension, icon: DescriptionOutlined, tone: "document" };
  return { extension, icon: InsertDriveFileOutlined, tone: "file" };
}

export default function SchoolResources() {
  const alert = useAlert();
  const { selectedChild } = useParent();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const classId = getClassId(selectedChild);
  const className =
    selectedChild?.student?.student_class?.name || "Student class";
  const studentName = selectedChild?.student?.name || "Student";

  const loadResources = async () => {
    if (!classId) return;
    setLoading(true);
    setError("");
    setResources([]);
    try {
      const token = localStorage.getItem("parentToken");
      const response = await fetch(`${baseUrl}/student-resource`, {
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      const result = await response.json();
      if (!response.ok)
        throw new Error(result?.message || "Unable to load school resources.");
      const payload = result?.payload || result;
      const matchingResources = Array.isArray(payload)
        ? payload.filter((resource) =>
            (resource.student_classes || []).some(
              (item) => String(item.student_class?.id) === String(classId),
            ),
          )
        : [];
      if (!matchingResources.length) {
        const message = `No school resources found for ${className}.`;
        setError(message);
        alert.show(message);
        return;
      }
      setResources(matchingResources);
    } catch (requestError) {
      const message =
        requestError.message || "Unable to load school resources.";
      setError(message);
      alert.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadResources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId]);

  const visibleResources = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return resources;
    return resources.filter(
      (resource) =>
        String(resource.title || "")
          .toLowerCase()
          .includes(query) ||
        (resource.files || []).some((file) =>
          String(file.file || "")
            .toLowerCase()
            .includes(query),
        ),
    );
  }, [resources, search]);

  const totalFiles = useMemo(
    () =>
      resources.reduce(
        (total, resource) => total + (resource.files || []).length,
        0,
      ),
    [resources],
  );

  return (
    <section className="parent-progress-page parent-resources-page">
      <div className="parent-progress-hero parent-resources-hero">
        <div>
          <span className="parent-progress-eyebrow">Learning material</span>
          <h1>School Resources</h1>
          <p>
            {selectedChild
              ? `Files and learning material shared for ${studentName} — ${className}.`
              : "Select a child to view class resources."}
          </p>
        </div>
        <div
          className="parent-progress-hero-stat"
          aria-label={`${totalFiles} files`}
        >
          <span>{totalFiles}</span>
          <small>Available files</small>
        </div>
      </div>

      {!classId ? (
        <PageState
          icon={SchoolOutlined}
          title="No class selected"
          text="The selected child does not have class information."
        />
      ) : loading ? (
        <PageState
          loading
          title="Loading resources"
          text={`Getting learning material for ${className}.`}
        />
      ) : error ? (
        <PageState
          icon={ErrorOutline}
          error
          title="Resources unavailable"
          text={error}
          onRetry={loadResources}
        />
      ) : (
        <>
          <div className="parent-resources-toolbar">
            <div>
              <h2>Resources for {className}</h2>
              <p>
                {visibleResources.length} resource collection
                {visibleResources.length === 1 ? "" : "s"}
              </p>
            </div>
            <label>
              <Search />
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search resources"
                aria-label="Search resources"
              />
            </label>
          </div>

          {visibleResources.length ? (
            <div className="parent-resources-grid">
              {visibleResources.map((resource) => (
                <article className="parent-resource-card" key={resource.id}>
                  <header>
                    <div className="parent-resource-folder">
                      <FolderOpenOutlined />
                    </div>
                    <div>
                      <h3>{resource.title || "School resource"}</h3>
                      <p>{resource.campus?.name || "School campus"}</p>
                    </div>
                  </header>
                  <div className="parent-resource-files">
                    {(resource.files || []).length ? (
                      (resource.files || []).map((file) => (
                        <ResourceFile file={file} key={file.id} />
                      ))
                    ) : (
                      <p className="parent-resource-no-files">
                        No files attached.
                      </p>
                    )}
                  </div>
                  <footer>
                    <span>
                      {(resource.files || []).length} file
                      {(resource.files || []).length === 1 ? "" : "s"}
                    </span>
                    <span>{className}</span>
                  </footer>
                </article>
              ))}
            </div>
          ) : (
            <div className="parent-resources-empty">
              <Search />
              <h2>No matching resources</h2>
              <p>Try changing your search text.</p>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function ResourceFile({ file }) {
  const info = fileInfo(file.file);
  const Icon = info.icon;
  const fileUrl = `${baseUrl.replace(
    "/api",
    "",
  )}/storage/student/resources/${file.file}`;
  return (
    <a
      href={fileUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={`parent-resource-file tone-${info.tone}`}
    >
      <span className="parent-resource-file-icon">
        <Icon />
      </span>
      <span className="parent-resource-file-name">
        <strong>{file.file || "Resource file"}</strong>
        <small>
          {info.extension} · Added {formatDate(file.created_at)}
        </small>
      </span>
      <span className="parent-resource-download">
        <CloudDownloadOutlined />
      </span>
    </a>
  );
}

function PageState({ icon: Icon, title, text, loading, error, onRetry }) {
  return (
    <div
      className={`parent-resources-state${error ? " error" : ""}`}
      role={error ? "alert" : "status"}
    >
      {loading ? (
        <span className="parent-resources-spinner" />
      ) : (
        Icon && <Icon />
      )}
      <h2>{title}</h2>
      <p>{text}</p>
      {onRetry && (
        <button type="button" onClick={onRetry}>
          <Refresh /> Try again
        </button>
      )}
    </div>
  );
}
